import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { RowActionButtons } from '@/components/tables/RowActionButtons';
import { Modal } from '@/components/dialogs/Modal';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { TextField } from '@/components/forms/TextField';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import type { TenantWhiteListedIpRow } from '@/api/services/tenants';
import {
  useCreateWhiteListedIp,
  useDeleteWhiteListedIp,
  useUpdateWhiteListedIp,
  useWhiteListedIps,
} from '../api';

interface IpForm {
  ip: string;
}
type ModalState = { mode: 'create' } | { mode: 'edit'; row: TenantWhiteListedIpRow } | null;

export function WhiteListedIpTab({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const listQuery = useWhiteListedIps(tenantId);
  const createMut = useCreateWhiteListedIp(tenantId);
  const updateMut = useUpdateWhiteListedIp(tenantId);
  const deleteMut = useDeleteWhiteListedIp(tenantId);

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<TenantWhiteListedIpRow | null>(null);
  const { register, handleSubmit, reset } = useForm<IpForm>();

  function openCreate() {
    reset({ ip: '' });
    setModal({ mode: 'create' });
  }
  function openEdit(row: TenantWhiteListedIpRow) {
    reset({ ip: row.ip });
    setModal({ mode: 'edit', row });
  }

  function onSubmit(values: IpForm) {
    const done = {
      onSuccess: () => {
        toast.success('IP saved.');
        setModal(null);
      },
      onError: (err: unknown) => toast.error(err instanceof ApiError ? err.message : 'Failed to save IP.'),
    };
    if (modal?.mode === 'create') {
      createMut.mutate(values.ip.trim(), done);
    } else if (modal?.mode === 'edit') {
      updateMut.mutate({ id: modal.row.id, ip: values.ip.trim() }, done);
    }
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteMut.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('IP deleted.');
        setDeleteTarget(null);
      },
      onError: (err) => {
        toast.error(err instanceof ApiError ? err.message : 'Failed to delete.');
        setDeleteTarget(null);
      },
    });
  }

  if (listQuery.isLoading) return <Spinner />;
  if (listQuery.isError) return <ErrorState onRetry={() => listQuery.refetch()} />;

  const rows = listQuery.data?.items ?? [];
  const busy = createMut.isPending || updateMut.isPending;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>WhiteListed IPs</h5>
        <button type="button" className="btn btn-primary" title="Add new record" onClick={openCreate}>
          <i className="fa fa-plus me-1" />
          Add New
        </button>
      </div>

      <div className="table-responsive">
      <table className="table dataTable table-striped table-hover w-100 mb-0">
        <thead>
          <tr>
            <th className="text-start">IP</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={2}>
                <EmptyState message="No whitelisted IPs." />
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id}>
                <td className="text-start">{row.ip}</td>
                <td>
                  <RowActionButtons onEdit={() => openEdit(row)} onDelete={() => setDeleteTarget(row)} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>

      {modal && (
        <Modal
          show
          title={modal.mode === 'create' ? 'Add WhiteListed IP' : 'Edit WhiteListed IP'}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="btn btn-dark" onClick={() => setModal(null)} disabled={busy}>
                Cancel
              </button>
              <button type="submit" form="ip-form" className="btn btn-primary" disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <form id="ip-form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <TextField label="IP" {...register('ip')} />
          </form>
        </Modal>
      )}

      <ConfirmDialog
        show={deleteTarget !== null}
        title="Delete IP"
        message={`Delete "${deleteTarget?.ip ?? ''}"?`}
        confirmLabel="Delete"
        busy={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
